/** Constants used to fill up our data base. */

import { Injectable } from '@angular/core';
import { labFeaturesEndpoints } from '@lab/features/lab-features.endpoints';
import { MpsRepository } from '@mps/mps-kit/mps-mock/repos/mps-repository';
import { MpsMockBaseService } from '@mps/mps-kit/mps-mock/services/mps-mock.service';
import { GeneratorMap, MpsGenerator } from '../../../../mps/mps-kit/mps-mock/helpers/mps-generator';
import { Zouma } from '../models/zouma.interface';

@Injectable({
  providedIn: 'root',
})
export class ZoumasFakerService extends MpsMockBaseService<Zouma> {
  repository = new MpsRepository<Zouma>();

  constructor() {
    super();
    this.repository.fill(this.generateMany(100065));
  }

  public getBaseRoute(): string {
    return labFeaturesEndpoints.zoumas;
  }

  /**
   * Définition des routes mockées avec leurs handlers
   */
  protected getRoutes(): Record<string, any> {
    return {
      // GET /zoumas/{id} - Récupérer un zouma par ID
      'GET::/zoumas/{id}': (params: any) => {
        const id = Number(params.routeParams.id);
        return this.repository.findById(id);
      },

      // GET /zoumas - Récupérer tous les zoumas (avec pagination)
      'GET::/zoumas': (params: any) => {
        const page = Number(params.queryParams.page) || 1;
        const limit = Number(params.queryParams.limit) || 10;
        const allData = this.repository.findAll();

        const startIndex = (page - 1) * limit;
        const endIndex = startIndex + limit;
        const paginatedData = allData.slice(startIndex, endIndex);

        return {
          data: paginatedData,
          total: allData.length,
          page: page,
          limit: limit,
        };
      },

      // POST /zoumas - Créer un nouveau zouma
      'POST::/zoumas': (params: any, payload: any) => {
        this.repository.create(payload);
        return payload;
      },

      // PUT /zoumas/{id} - Mettre à jour un zouma
      'PUT::/zoumas/{id}': (params: any, payload: any) => {
        const id = Number(params.routeParams.id);
        return this.repository.update(id, payload);
      },

      // DELETE /zoumas/{id} - Supprimer un zouma
      'DELETE::/zoumas/{id}': (params: any) => {
        const id = Number(params.routeParams.id);
        this.repository.delete(id);
        return { success: true, message: `Zouma ${id} deleted` };
      },

      // GET /zoumas/search - Recherche personnalisée
      'GET::/zoumas/search': (params: any) => {
        const query = (params.queryParams.q as string) || '';
        const filtered = this.repository
          .findAll()
          .filter(
            (z) =>
              z.firstname.toLowerCase().includes(query.toLowerCase()) ||
              z.lastname.toLowerCase().includes(query.toLowerCase()) ||
              z.email.toLowerCase().includes(query.toLowerCase())
          );

        return {
          data: filtered,
          total: filtered.length,
          page: 1,
          limit: filtered.length,
        };
      },
    };
  }

  public generateOne(): Zouma {
    const zoumaMpa: GeneratorMap<Zouma> = {
      id: () => Math.floor(Math.random() * 10000),
      firstname: () => MpsGenerator.firstName(),
      lastname: () => MpsGenerator.lastName(),
      igg: () => MpsGenerator.igg(),
      email: (ctx) => (ctx?.firstname ?? '').toLowerCase() + '.' + (ctx?.lastname ?? '').toLowerCase() + '@example.com',
      role: () => MpsGenerator.pick(['Admin', 'User', 'Manager', 'Guest'])(),
      isActive: () => MpsGenerator.boolean(),
      isExtern: () => MpsGenerator.boolean(),
      lastLogin: () => MpsGenerator.date(),
      createdAt: () => MpsGenerator.date(),
      updatedAt: () => MpsGenerator.date(),
      job: () => MpsGenerator.job(),
      gender: () => MpsGenerator.pick(['M', 'F'])(),
      icon: () => 'person',
      avatar: () => 'https://api.dicebear.com/9.x/pixel-art/svg?seed=' + Math.floor(Math.random() * 100),
    };
    return MpsGenerator.generateFromMap(zoumaMpa);
  }
}
